import PageLoader from '@components/PageLoader'
import { useAdminItineraryImport } from '@hooks/useAdminItineraryImport'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { formatDateTime, formatName } from '../adminFormat'
import {
	BackLink,
	Card,
	CardTitle,
	DetailHeader,
	DetailTitle,
	Field,
	FieldGrid,
	FieldLabel,
	FieldValue,
	InlineLink,
	RelatedList,
	RelatedMeta,
	RelatedRow,
} from '../adminDetail.styles'
import { EmptyNoteInline } from '@components/EmptyState'
import { ErrorNote } from '@components/Notice'
import { StatusPill } from './ImportsPage.styles'
import {
	CardHeaderRow,
	FileCaption,
	FileGrid,
	FilePlaceholder,
	FileTile,
	TextBlock,
	Thumbnail,
	ToggleButton,
} from './ImportDetailPage.styles'
import { createdEventsTitle, fileCaption, fileKind, formatRawResponse, statusLabel, statusTone } from './importView'

/**
 * Read-only admin detail for one itinerary import: who asked and on which trip,
 * what they sent (pasted text and uploaded files), why it failed if it did, the
 * events it created (deleted ones flagged — usually the ones unticked at review),
 * and, behind a toggle, Claude's raw response. No mutations.
 */
const ImportDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>()
	const { data: detail, isLoading, isError } = useAdminItineraryImport(id)
	const [showRaw, setShowRaw] = useState(false)

	return (
		<>
			<BackLink to="/admin/itinerary-imports">← Back to imports</BackLink>

			{isLoading ? (
				<PageLoader label="Loading import…" />
			) : isError || !detail ? (
				<ErrorNote>Could not load this import.</ErrorNote>
			) : (
				<>
					<DetailHeader>
						<DetailTitle>Itinerary import</DetailTitle>
						<StatusPill $tone={statusTone(detail.status)}>{statusLabel(detail.status)}</StatusPill>
					</DetailHeader>

					<Card>
						<CardTitle>Import</CardTitle>
						<FieldGrid>
							<Field>
								<FieldLabel>Trip</FieldLabel>
								<FieldValue>
									<InlineLink to={`/admin/trips/${detail.trip.id}`}>
										{detail.trip.name || 'Untitled trip'}
									</InlineLink>
								</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Requested by</FieldLabel>
								<FieldValue>
									<InlineLink to={`/admin/users/${detail.user.id}`}>
										{formatName(detail.user.firstName, detail.user.lastName)}
									</InlineLink>
									{' · '}
									{detail.user.email}
								</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Created</FieldLabel>
								<FieldValue>{formatDateTime(detail.createdAt)}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Last updated</FieldLabel>
								<FieldValue>{formatDateTime(detail.lastUpdatedAt)}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Import ID</FieldLabel>
								<FieldValue>{detail.id}</FieldValue>
							</Field>
						</FieldGrid>
					</Card>

					{detail.errorReason && (
						<Card>
							<CardTitle>Error</CardTitle>
							<TextBlock>{detail.errorReason}</TextBlock>
						</Card>
					)}

					{detail.textInput && (
						<Card>
							<CardTitle>Pasted text</CardTitle>
							<TextBlock>{detail.textInput}</TextBlock>
						</Card>
					)}

					<Card>
						<CardTitle>Uploaded files ({detail.files.length})</CardTitle>
						{detail.files.length === 0 ? (
							<EmptyNoteInline>No files were uploaded.</EmptyNoteInline>
						) : (
							<FileGrid>
								{detail.files.map((file, index) => {
									const caption = fileCaption(file, index)
									const kind = fileKind(file.contentType)
									return (
										<FileTile key={file.url} href={file.url} target="_blank" rel="noopener noreferrer">
											{kind === 'image' ? (
												<Thumbnail src={file.url} alt={caption} loading="lazy" />
											) : (
												<FilePlaceholder>{kind === 'other' ? 'Open file' : kind.toUpperCase()}</FilePlaceholder>
											)}
											<FileCaption>{caption}</FileCaption>
										</FileTile>
									)
								})}
							</FileGrid>
						)}
					</Card>

					<Card>
						<CardTitle>{createdEventsTitle(detail.createdEvents)}</CardTitle>
						{detail.createdEvents.length === 0 ? (
							<EmptyNoteInline>This import created no events.</EmptyNoteInline>
						) : (
							<RelatedList>
								{detail.createdEvents.map(event => (
									<RelatedRow key={event.id} to={`/admin/events/${event.id}`} $deleted={event.isDeleted}>
										<span>
											{event.name} · {event.category}
											{event.isDeleted && ' · deleted'}
										</span>
										<RelatedMeta>
											Day {event.tripDayIndex + 1}
											{event.startTime && ` · ${event.startTime}`}
										</RelatedMeta>
									</RelatedRow>
								))}
							</RelatedList>
						)}
					</Card>

					<Card>
						<CardHeaderRow>
							<CardTitle>Claude response</CardTitle>
							{detail.claudeRawResponse != null && (
								<ToggleButton onClick={() => setShowRaw(shown => !shown)} aria-expanded={showRaw}>
									{showRaw ? 'Hide raw response' : 'Show raw response'}
								</ToggleButton>
							)}
						</CardHeaderRow>
						{detail.claudeRawResponse == null ? (
							<EmptyNoteInline>No response was recorded.</EmptyNoteInline>
						) : (
							showRaw && <TextBlock>{formatRawResponse(detail.claudeRawResponse)}</TextBlock>
						)}
					</Card>
				</>
			)}
		</>
	)
}

export default ImportDetailPage
